/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 14 2024
 **/

import { useAtomCallback } from "jotai/utils";

import { useEventHandler } from "@rfice/eventhandler";
import { _T } from "@rfice/language";
import { JotaiStore } from "@/frameworks/libraries/commons";
import { EventID, SidebarID } from "@/frameworks/core/commons";
import { useSidebarActions } from "@/frameworks/core/hooks";
import { useSpace } from "@/frameworks/core/API";
import { useToast } from "@/frameworks/designStuff";

import { ManageSpaceMemberSpaceDetailAtom, SpaceManageMemberTabAtom } from "./atom";

export default function openManageSpaceMemberProvider() {
	const sidebarActions = useSidebarActions();
	const toast = useToast();
	const spaceAPI = useSpace();

	const handleUpdateAndShowSidebar = useAtomCallback(
		async (
			_,
			set,
			{
				spaceInfo,
				activeTab
			}: { spaceInfo: WebServices.Space.Detail.Data; activeTab?: "current-members" | "request-status" }
		) => {
			try {
				const { data } = await spaceAPI.detail.execute({ spaceId: spaceInfo.spaceId });
				if (data) {
					showManageMemberSidebar();
				}
				set(ManageSpaceMemberSpaceDetailAtom, data);
				activeTab && set(SpaceManageMemberTabAtom, activeTab);
			} catch (error) {
				toast.show({ type: "error", message: _T("스페이스 정보를 불러올 수 없습니다.") });
				console.error(error);
			}
		},
		{
			store: JotaiStore
		}
	);

	const showManageMemberSidebar = () => {
		sidebarActions.show(SidebarID.ManageSpaceMember);
	};

	const resetHandler = useAtomCallback(
		(_, set) => {
			set(ManageSpaceMemberSpaceDetailAtom, null);
		},
		{
			store: JotaiStore
		}
	);

	useEventHandler(EventID.OPEN_MANAGE_SPACE_MEMBER, handleUpdateAndShowSidebar);

	sidebarActions.useSidebarEffect(
		SidebarID.ManageSpaceMember,
		(args) => {
			if (!args.isOpen) {
				resetHandler();
			}
		},
		[]
	);

	return null;
}
